// src/services/authService/authMenu.ts
// 提供已认证场景下的菜单树获取 Hook，结合 react-admin 与 React Query 做缓存

import { useAuthenticated } from 'react-admin';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { queryCurrentUserMenuTree, type Menu } from '@/services/manageService/menuService';

const hasLocalToken = () => typeof localStorage !== 'undefined'
  && (!!localStorage.getItem('adminToken') || !!localStorage.getItem('Admin-Token'));

/** Single cache key: must match login prefetch / setQueryData (do not mix in permissions). */
export const AUTH_MENU_QUERY_KEY = ['authMenu'] as const;

// 约定：parentId 缺省时使用 0 作为根节点
export function useAuthMenu() {
  // DynamicAdminUI 在 /login 也会挂载本 hook。默认 logoutOnFailure=true 会在
  // checkAuth 失败时调用 logout()，与 login 写入 token 竞态，导致无法跳转 dashboard。
  // 路由鉴权由 AdminUI requireAuth 负责；此处仅在已有 token 时做探测，失败不登出。
  useAuthenticated({ logoutOnFailure: false, enabled: hasLocalToken() });

  // Token 即可拉菜单；不要等 getPermissions，否则 UAT 首屏侧栏会多一整段 RTT
  const enabled = hasLocalToken();

  const query = useQuery<Menu[], Error>({
    queryKey: AUTH_MENU_QUERY_KEY,
    queryFn: ({ signal }) => queryCurrentUserMenuTree(signal),
    // 缓存与新鲜度控制，避免每次路由跳转都重新请求
    staleTime: 5 * 60 * 1000, // 5 分钟内视为新鲜
    gcTime: 30 * 60 * 1000, // 30 分钟内保留缓存（TanStack Query v5，原 cacheTime）
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });

  return {
    ...query,
    isLoading: query.isLoading,
  };
}

// 便捷方法：在命令式场景下直接取一次（不缓存）
export async function fetchAuthMenu() {
  return queryCurrentUserMenuTree();
}

// 预取菜单树（用于登录后首屏优化）；key 必须与 useAuthMenu 一致
export function prefetchAuthMenu(queryClient: QueryClient) {
  return queryClient.prefetchQuery({
    queryKey: AUTH_MENU_QUERY_KEY,
    queryFn: ({ signal }) => queryCurrentUserMenuTree(signal),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

// 清空菜单缓存（用于登出）
export function clearAuthMenuCache(queryClient: QueryClient) {
  queryClient.cancelQueries({ queryKey: ['authMenu'] });
  return queryClient.removeQueries({ queryKey: ['authMenu'] });
}
